import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import VINDecoder from '../components/VINDecoder';
import { Button } from '../components/ui/button';
import { Car, Search, ArrowRight } from 'lucide-react';

const VINLookupPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [decodedVehicle, setDecodedVehicle] = useState(null);

  const handleVehicleDecoded = (vehicle) => {
    setDecodedVehicle(vehicle);
    
    localStorage.setItem('decodedVehicle', JSON.stringify(vehicle));
  };

  const handleSearchParts = () => {
    if (decodedVehicle) {
      navigate(`/products?year=${decodedVehicle.year}&make=${decodedVehicle.make}&model=${decodedVehicle.model}`);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="bg-orange-100 p-4 rounded-full">
              <Car className="h-12 w-12 text-orange-600" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-3">
            {t('vin.title')}
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t('vin.subtitle')}
          </p>
        </div>

        {/* VIN Decoder Component */}
        <div className="mb-8">
          <VINDecoder 
            onVehicleDecoded={handleVehicleDecoded}
            showFullInfo={true}
          />
        </div>

        {/* Search Parts Button */}
        {decodedVehicle && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-semibold mb-3 text-center">
              {t('vin.ready_to_find', { year: decodedVehicle.year, make: decodedVehicle.make, model: decodedVehicle.model })}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('vehicle.region')}</label>
                <select id="region" className="w-full border rounded px-3 py-2" defaultValue="GCC">
                  <option value="USA">USA</option>
                  <option value="GCC">GCC</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('vehicle.trim')}</label>
                <input id="trim" className="w-full border rounded px-3 py-2" placeholder={t('vin.enter_trim')} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('vehicle.engine')}</label>
                <input id="engine" className="w-full border rounded px-3 py-2" placeholder={t('vin.enter_engine')} />
              </div>
            </div>

            <div className="text-center">
              <Button 
                onClick={() => {
                  const region = document.getElementById('region').value;
                  const trim = document.getElementById('trim').value.trim();
                  const engine = document.getElementById('engine').value.trim();
                  if (!region || !trim || !engine) return;
                  const v = { region, year: decodedVehicle.year, make: decodedVehicle.make, model: decodedVehicle.model, trim, engine };
                  localStorage.setItem('fitmentSelection', JSON.stringify(v));
                  const params = new URLSearchParams({
                    vehicle_region: region,
                    vehicle_year: String(decodedVehicle.year),
                    vehicle_make: decodedVehicle.make,
                    vehicle_model: decodedVehicle.model,
                    vehicle_trim: trim,
                    vehicle_engine: engine,
                  });
                  navigate(`/products?${params.toString()}`);
                }}
                size="lg"
                className="bg-orange-600 hover:bg-orange-700"
              >
                <Search className="mr-2 h-5 w-5" />
                {t('vin.search_compatible')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        )}

        {/* Info Section */}
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <div className="bg-blue-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
              <span className="text-2xl">🔍</span>
            </div>
            <h3 className="font-semibold text-lg mb-2">{t('vin.accurate_decoding')}</h3>
            <p className="text-gray-600 text-sm">
              {t('vin.decoding_description')}
            </p>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <div className="bg-green-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
              <span className="text-2xl">⚡</span>
            </div>
            <h3 className="font-semibold text-lg mb-2">{t('vin.instant_results')}</h3>
            <p className="text-gray-600 text-sm">
              {t('vin.results_description')}
            </p>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <div className="bg-orange-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
              <span className="text-2xl">🎯</span>
            </div>
            <h3 className="font-semibold text-lg mb-2">{t('vin.perfect_match')}</h3>
            <p className="text-gray-600 text-sm">
              {t('vin.match_description')}
            </p>
          </div>
        </div>

        {/* Where to find VIN */}
        <div className="mt-12 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
            <span>📋</span>
            {t('vin.where_find_vin')}
          </h3>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start gap-2">
              <span className="font-semibold min-w-[140px]">{t('vin.dashboard')}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-semibold min-w-[140px]">{t('vin.door')}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-semibold min-w-[140px]">{t('vin.documents')}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-semibold min-w-[140px]">{t('vin.engine_block')}</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default VINLookupPage;
